import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import shortid from 'shortid';
import dayjs from 'dayjs';
import Form from '@/common/components/Form/index';
import { findQuestion } from '@/common/containers/FormConstructor/utils';
import { FieldsConstructor } from '@/common/containers/FormConstructor/components/index';
import { useQuery } from '@/common/hooks/index';

const findQuestionById = (questions = [], questionId) => {
  for (const question of questions) {
    if (question.id === questionId) {
      return question;
    }

    const childQuestion = findQuestionById(question.children || [], questionId);

    if (childQuestion) {
      return childQuestion;
    }
  }

  return null;
};

const FormConstructorContext = createContext({
  questions: [],
  answers: [],
  allAnswers: {},
  archivedAnswers: {},
  relativeAnswers: {},
});

const FormConstructor = ({
  className,
  formId,
  onSubmit,
  questions = [],
  answers,
  validation,
  onFormChange,
  formRef,
  ...props
}) => {
  const query = useQuery();
  const localRef = useRef(null);
  const {
    updatedSavedAnswers,
    archivedAnswers,
    initialFields,
    relativeAnswers,
  } = useMemo(() => {
    const questionCache = {};
    const findSlug = (slug) => {
      let res = null;
      if (questionCache[slug]) return questionCache[slug];

      res = findQuestion(slug, questions);
      questionCache[slug] = res;
      return res;
    };

    const res = (answers || []).reduce(
      (acc, { questionId, slug, value, id, relatedAnswers, status }) => {
        const slugInfo = findSlug(slug);
        const uid = shortid.generate();
        const field = {
          value,
        };
        const payload = {
          value,
          answerId: id,
          uid,
          questionId,
        };

        const isArchivedDataEntry =
          status === 'suspended' &&
          slugInfo?.parent &&
          !['multiple', 'DecCustodyInfo', 'single'].includes(
            slugInfo.answerType
          );

        if (isArchivedDataEntry) {
          if (!slugInfo.answerOptions?.can_add_new) {
            return acc;
          }

          if (!acc.archivedAnswers[slug]) {
            acc.archivedAnswers[slug] = {};
          }

          acc.archivedAnswers[slug][uid] = payload;
          return acc;
        }

        if (
          status === 'suspended' &&
          (!relatedAnswers.length || value === 'Yes' || value === 'No')
        ) {
          return acc;
        }

        if (!slugInfo) {
          if (!relatedAnswers.length) {
            if (!acc.relativeAnswers[slug]) {
              acc.relativeAnswers[slug] = payload;
            } else if ('value' in acc.relativeAnswers[slug]) {
              const existingAnswer = acc.relativeAnswers[slug];
              acc.relativeAnswers[slug] = {
                [existingAnswer.uid]: existingAnswer,
                [uid]: payload,
              };
            } else {
              acc.relativeAnswers[slug][uid] = payload;
            }
          } else {
            if (!acc.relativeAnswers[slug]) {
              acc.relativeAnswers[slug] = {};
            }
            acc.relativeAnswers[slug][uid] = payload;
          }
          return acc;
        }

        if (!acc.updatedSavedAnswers[slug]) {
          acc.updatedSavedAnswers[slug] = {};
          acc.initialFields[slug] = {};
        }

        if (slugInfo.answerType === 'single') {
          acc.updatedSavedAnswers[slug] = payload;
          acc.initialFields[slug] = field;
        } else if (slugInfo.answerType === 'multiple') {
          acc.updatedSavedAnswers[slug][value] = payload;
          acc.initialFields[slug][value] = field;
        } else if (!slugInfo.parent) {
          acc.updatedSavedAnswers[slug] = payload;
          acc.initialFields[slug] = field;
        } else {
          acc.updatedSavedAnswers[slug][uid] = payload;
          acc.initialFields[slug][uid] = field;
        }

        return acc;
      },
      {
        updatedSavedAnswers: {},
        archivedAnswers: {},
        initialFields: {},
        relativeAnswers: {},
      }
    );
    return res;
  }, [answers, questions]);

  const updatedQuestions = useMemo(() => {
    const updateChildId = (arr) => {
      return arr.reduce((acc, quest) => {
        const { answerType } = quest;
        if (
          !updatedSavedAnswers[quest.slug] ||
          ['multiple', 'DecCustodyInfo', 'single'].includes(answerType)
        ) {
          acc.push({
            ...quest,
            uid: shortid.generate(),
            children: updateChildId(quest.children),
          });
          return acc;
        }

        /**
         * adds additional dataEntry questions objects which were saved
         */
        Object.keys(updatedSavedAnswers[quest.slug]).forEach((key) => {
          acc.push({
            ...quest,
            uid: key,
            children: updateChildId(quest.children),
          });
        });

        return acc;
      }, []);
    };

    return questions.map((quest) => ({
      ...quest,
      uid: shortid.generate(),
      children: updateChildId(quest.children),
    }));
  }, [questions, updatedSavedAnswers]);

  const formatSubmitValues = useCallback(
    (form) => {
      /**
       * @description Unpacking form payload, similar to Array.flat
       */
      const formatForm = (form) => {
        return Object.entries(form).reduce((acc, item) => {
          const [slug, result] = item;
          if (slug === '__archivedActions') {
            return acc;
          }

          if (result.value) {
            acc.push({ slug, answer: result });
          } else if (!('value' in result) && Object.keys(result).length > 0) {
            Object.entries(result).forEach(([uid, answer]) => {
              if (answer.value || answer.__action)
                acc.push({ slug, answer, uid });
            });
          }
          return acc;
        }, []);
      };

      /**
       * @description Unpacking form payload, similar to Array.flat
       */
      const compareToInitialValues = (form) => {
        const archivedActions = form.__archivedActions || {};
        const answersToDelete = Object.entries(updatedSavedAnswers).reduce(
          (acc, [slug, value]) => ({
            ...acc,
            [slug]:
              value && typeof value === 'object' && !('value' in value)
                ? { ...value }
                : value,
          }),
          {}
        );
        const isEqualValue = (left, right) =>
          JSON.stringify(left) === JSON.stringify(right);

        Object.entries(archivedActions).forEach(([slug, items]) => {
          Object.keys(items || {}).forEach((uid) => {
            if (answersToDelete[slug]?.[uid]) {
              delete answersToDelete[slug][uid];
            }
          });
        });

        const normalizeAnswerValue = (value) => {
          if (value instanceof Date) {
            return dayjs(value).isValid()
              ? `${dayjs(value).format('YYYY-MM-DD')}T00:00:00`
              : '';
          }

          if (Array.isArray(value)) {
            return value.map(normalizeAnswerValue);
          }

          if (value && typeof value === 'object') {
            return Object.entries(value).reduce(
              (acc, [key, itemValue]) => ({
                ...acc,
                [key]: normalizeAnswerValue(itemValue),
              }),
              {}
            );
          }

          return value;
        };

        /**
         * Formatted form values to API format
         */
        const payload = form
          .map(({ slug, answer, uid }) => {
            if (
              uid &&
              archivedAnswers[slug]?.[uid] &&
              answer.__action !== 'restore'
            ) {
              return null;
            }

            const value = normalizeAnswerValue(answer.value);
            const question = findQuestion(slug, updatedQuestions);

            /**
         if hasInitialValue is true we should update initial answer
         Remove answer from list for delete if slug exist in form.
         */
            const hasInitialValue = uid
              ? updatedSavedAnswers[slug] && updatedSavedAnswers[slug][uid]
              : updatedSavedAnswers[slug];
            if (hasInitialValue) {
              uid
                ? delete answersToDelete[slug][uid]
                : delete answersToDelete[slug];
            }

            if (answer.__action === 'restore') {
              const initialValue = normalizeAnswerValue(
                hasInitialValue?.value ?? answer.value
              );

              if (isEqualValue(value, initialValue)) {
                return {
                  questionId: answer.questionId || question.id,
                  answerId: answer.answerId || hasInitialValue?.answerId,
                  value,
                  action: 'restore',
                };
              }

              return {
                questionId: question.id,
                answerId: answer.answerId || hasInitialValue?.answerId,
                value,
                action: 'update',
              };
            }

            const action = ['single', 'multiple'].includes(question.answerType)
              ? 'create'
              : hasInitialValue
              ? 'update'
              : 'create';

            const payload = {
              questionId: question.id,
              value,
              action,
              answerId: action === 'update' ? hasInitialValue.answerId : null,
            };
            return payload;
          })
          .filter(Boolean);

        /**
         * Add to payload redundant answers to delete them on BE
         */
        Object.values(answersToDelete).forEach((item) => {
          const questionWasInForm = (item) =>
            !!payload.find(({ questionId }) => item.questionId === questionId);
          const pushItem = ({ questionId, answerId }) => {
            const question = findQuestionById(updatedQuestions, questionId);
            const action = question?.answerOptions?.can_add_new
              ? 'archive'
              : 'delete';

            payload.push({
              questionId,
              answerId,
              value: '',
              action,
            });
          };

          if (item.value && questionWasInForm(item)) {
            pushItem(item);
          } else {
            Object.values(item).forEach((item) => {
              if (questionWasInForm(item)) pushItem(item);
            });
          }
        });

        Object.values(archivedActions).forEach((items) => {
          Object.values(items || {}).forEach((item) => {
            payload.push(item);
          });
        });

        return payload;
      };

      return compareToInitialValues(formatForm(form));
    },
    [archivedAnswers, updatedSavedAnswers, updatedQuestions]
  );

  const handleSubmit = useCallback(
    (form = {}) => {
      const payload = formatSubmitValues(form);

      return onSubmit(payload);
    },
    [formatSubmitValues, onSubmit]
  );

  useEffect(() => {
    if (!localRef.current) return;
    localRef.current = {
      ...localRef.current,
      getShapedValues: () => {
        const values = localRef.current.getValues();
        return formatSubmitValues(values);
      },
    };
    if (formRef) formRef.current = localRef.current;
  }, [formRef, formatSubmitValues]);

  const contextValue = useMemo(
    () => ({
      questions: updatedQuestions,
      relativeAnswers,
      allAnswers: updatedSavedAnswers,
      archivedAnswers,
    }),
    [archivedAnswers, updatedQuestions, relativeAnswers, updatedSavedAnswers]
  );

  useEffect(() => {
    const querySlug = query.get('slug');
    if (querySlug) {
      setTimeout(() => {
        const element = document.getElementById(`${querySlug}-anchor`);
        if (element)
          element.scrollIntoView({ behavior: `smooth`, block: 'center' });
      }, 1000);
    }
  }, [query]);

  return (
    <Form
      formRef={localRef}
      className={className}
      initialValues={initialFields}
      onSubmit={handleSubmit}
      id={formId}
      validation={validation}
      {...props}
    >
      <FormConstructorContext.Provider value={contextValue}>
        <FieldsConstructor
          questions={updatedQuestions}
          onFormChange={onFormChange}
        />
      </FormConstructorContext.Provider>
    </Form>
  );
};

export { FormConstructorContext };
export default FormConstructor;
